import {
	Alert,
	AppBar,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	Divider,
	FormControl,
	IconButton,
	InputLabel,
	List,
	ListItem,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextField,
	Toolbar,
	Typography,
} from '@mui/material'
import Transition from 'components/Transition'
import CloseIcon from '@mui/icons-material/Close'
import SaveAsTwoToneIcon from '@mui/icons-material/SaveAsTwoTone'
import PhonelinkEraseIcon from '@mui/icons-material/PhonelinkErase'
import moment from 'moment'
import { Box } from '@mui/system'
import { useEffect, useState } from 'react'
import { alertModal } from 'util/util'
import { SellerInfoType, DocumentsType, StatusType } from 'constants/appType'
import { apiShopSellerDocuments } from 'api/api'

type SurveyPopupProps = {
	isOpen: boolean
	onClose: () => void
	sellerInfo: SellerInfoType | null
	sellerDocuments: DocumentsType[]
	save: (
		commissionRate: number,
		status: keyof StatusType,
		reason: string,
	) => void
}

const ShopUserInfo = ({
	isOpen,
	onClose,
	sellerInfo,
	sellerDocuments,
	save,
}: SurveyPopupProps) => {
	const STATUS_OPTIONS: { value: keyof StatusType; label: string }[] = [
		{ value: 'PENDING', label: '대기' },
		{ value: 'APPROVED', label: '승인' },
		{ value: 'REJECTED', label: '거절' },
		{ value: 'SUSPENDED', label: '정지' },
	]

	const [selectedStatus, setSelectedStatus] = useState<keyof StatusType>(
		(sellerInfo?.status as unknown as keyof StatusType) ?? 'PENDING',
	)

	const [commissionRate, setCommissionRate] = useState<number>(
		sellerInfo?.commissionRate ?? 0,
	)

	const [reason, setReason] = useState<string>(sellerInfo?.reason ?? '')

	const isReasonRequired =
		selectedStatus === 'REJECTED' || selectedStatus === 'SUSPENDED'

	useEffect(() => {
		if (!isOpen) {
			return
		}

		setCommissionRate(sellerInfo?.commissionRate ?? 0)
		setSelectedStatus(
			(sellerInfo?.status as unknown as keyof StatusType) ?? 'PENDING',
		)
		setReason(sellerInfo?.reason ?? '')
	}, [isOpen, sellerInfo])

	useEffect(() => {
		if (!isReasonRequired && reason) {
			setReason('')
		}
	}, [isReasonRequired, reason])

	const [downloadingDocumentId, setDownloadingDocumentId] = useState<
		number | null
	>(null)

	const infoFields = [
		{ label: '아이디', value: sellerInfo?.username },
		{ label: '이름', value: sellerInfo?.name },
		{ label: '이메일', value: sellerInfo?.email },
		{ label: '연락처', value: sellerInfo?.phone },
		{ label: '상호명', value: sellerInfo?.businessName },
		{ label: '대표자명', value: sellerInfo?.representativeName },
		{ label: '사업자등록번호', value: sellerInfo?.businessNumber },
		{
			label: '사업자 인증',
			value: sellerInfo?.isBusinessNumberAuth ? '인증 완료' : '미인증',
		},
		{ label: '업태', value: sellerInfo?.businessType },
		{ label: '업종', value: sellerInfo?.businessCategory },
		{ label: '은행명', value: sellerInfo?.bankName },
		{ label: '예금주', value: sellerInfo?.bankHolder },
		{ label: '계좌번호', value: sellerInfo?.bankAccount },
		{
			label: '승인일시',
			value: sellerInfo?.approvedAt
				? moment(sellerInfo.approvedAt).format('YYYY-MM-DD HH:mm')
				: '-',
		},
		{
			label: '등록일시',
			value: sellerInfo?.createdAt
				? moment(sellerInfo.createdAt).format('YYYY-MM-DD HH:mm')
				: '-',
		},
		{
			label: '수정일시',
			value: sellerInfo?.updatedAt
				? moment(sellerInfo.updatedAt).format('YYYY-MM-DD HH:mm')
				: '-',
		},

		{
			label: '우편번호',
			value: sellerInfo?.zipCode || '',
		},
		{
			label: '주소',
			value: sellerInfo?.address || '',
		},
		{
			label: '주소 상세',
			value: sellerInfo?.addressDetail || '',
		},
	]

	const documentTypeLabelMap: Record<string, string> = {
		BUSINESS_LICENSE: '사업자등록증',
		BANK_ACCOUNT: '통장 사본',
		ID_CARD: '신분증',
		OTHER: '기타 서류',
	}

	// 저장 처리
	const handleSave = () => {
		if (!sellerInfo) {
			alertModal('판매자 정보가 없습니다.', 'warning', () => {})
			return
		}

		save(commissionRate, selectedStatus, reason)
	}

	// 파일 다운로드 처리
	const getFile = async (document: DocumentsType) => {
		if (!sellerInfo?.sellerId) {
			alertModal('판매자 정보가 없습니다.', 'warning', () => {})
			return
		}

		try {
			setDownloadingDocumentId(document.documentId)
			await apiShopSellerDocuments({
				documentId: document.documentId,
				documentType: document.documentType,
				sellerId: sellerInfo.sellerId,
				download: true,
			})
		} catch (error) {
			alertModal('문서 다운로드 중 오류가 발생했습니다.', 'error', () => {})
		} finally {
			setDownloadingDocumentId(null)
		}
	}

	return (
		<Dialog
			open={isOpen}
			onClose={onClose}
			fullScreen
			slots={{
				transition: Transition,
			}}
		>
			<AppBar sx={{ position: 'sticky' }}>
				<Toolbar>
					<Typography
						sx={{ flex: 1 }}
						variant="h6"
						component="div"
					>
						{sellerInfo?.businessName} - 판매자 정보
					</Typography>
					<IconButton
						edge="end"
						color="inherit"
						onClick={onClose}
						aria-label="close"
					>
						<CloseIcon />
					</IconButton>
				</Toolbar>
			</AppBar>
			<DialogContent>
				{sellerInfo ? (
					<Box sx={{ py: 3 }}>
						<Typography
							variant="h6"
							sx={{ mb: 2, fontWeight: 700 }}
						>
							판매자 기본 정보
						</Typography>
						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: {
									xs: '1fr',
									md: 'repeat(2, minmax(0, 1fr))',
								},
								gap: 2,
							}}
						>
							{infoFields.map((field) => (
								<TextField
									key={field.label}
									fullWidth
									label={field.label}
									value={field.value || '-'}
									InputProps={{ readOnly: true }}
									size="small"
								/>
							))}
						</Box>

						<Box
							sx={{
								mt: 3,
								p: 2.5,
								border: '2px solid',
								borderColor: 'primary.main',
								borderRadius: 2,
								bgcolor: 'primary.50',
								background: 'rgba(25, 118, 210, 0.04)',
							}}
						>
							<Box
								sx={{
									display: 'grid',
									gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
									gap: 2,
								}}
							>
								<TextField
									fullWidth
									label="수수료율 (%)"
									value={commissionRate}
									onChange={(e) => {
										const v = Number(e.target.value)
										if (!isNaN(v)) setCommissionRate(v)
									}}
									type="number"
									inputProps={{ min: 0, max: 100, step: 0.1 }}
									size="small"
								/>
								<FormControl
									fullWidth
									size="small"
								>
									<InputLabel id="seller-status-label">상태</InputLabel>
									<Select
										labelId="seller-status-label"
										label="상태"
										value={selectedStatus}
										onChange={(e: SelectChangeEvent) =>
											setSelectedStatus(e.target.value as keyof StatusType)
										}
									>
										{STATUS_OPTIONS.map((opt) => (
											<MenuItem
												key={opt.value}
												value={opt.value}
											>
												{opt.label}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Box>
							{isReasonRequired && (
								<TextField
									fullWidth
									label="사유"
									placeholder="거절/정지 사유를 입력하세요"
									value={reason}
									onChange={(e) => setReason(e.target.value)}
									multiline
									rows={3}
									size="small"
									sx={{ mt: 2 }}
									inputProps={{ name: 'reason' }}
								/>
							)}
						</Box>

						<Divider sx={{ my: 4 }} />

						<Typography
							variant="h6"
							sx={{ mb: 2, fontWeight: 700 }}
						>
							제출 서류
						</Typography>

						{sellerDocuments.length > 0 ? (
							<List sx={{ p: 0, border: '1px solid #e0e0e0', borderRadius: 2 }}>
								{sellerDocuments.map((document, index) => (
									<ListItem
										key={document.documentId}
										divider={index !== sellerDocuments.length - 1}
										sx={{
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: { xs: 'flex-start', sm: 'center' },
											gap: 2,
											flexDirection: { xs: 'column', sm: 'row' },
										}}
									>
										<Box>
											<Typography
												variant="subtitle1"
												sx={{ fontWeight: 600 }}
											>
												{documentTypeLabelMap[String(document.documentType)] ||
													String(document.documentType)}
											</Typography>
											{/* <Typography
												variant="body2"
												color="text.secondary"
											>
												문서 ID: {document.documentId}
											</Typography> */}
											<Typography
												variant="body2"
												color="text.secondary"
											>
												등록일시: {moment(document.createdAt).format('YYYY-MM-DD HH:mm')}
											</Typography>
										</Box>
										<Button
											variant="contained"
											size="small"
											onClick={() => getFile(document)}
											disabled={downloadingDocumentId === document.documentId}
										>
											{downloadingDocumentId === document.documentId
												? '다운로드 중...'
												: '다운로드'}
										</Button>
									</ListItem>
								))}
							</List>
						) : (
							<Alert severity="info">등록된 제출 서류가 없습니다.</Alert>
						)}
					</Box>
				) : (
					<Alert severity="warning">표시할 판매자 정보가 없습니다.</Alert>
				)}
			</DialogContent>
			<Divider />
			<DialogActions>
				<Button
					variant="contained"
					onClick={handleSave}
					color="primary"
					size="small"
					startIcon={<SaveAsTwoToneIcon />}
					disabled={!sellerInfo}
				>
					저장
				</Button>
				<Button
					variant="contained"
					onClick={onClose}
					color="secondary"
					size="small"
					startIcon={<PhonelinkEraseIcon />}
				>
					취소
				</Button>
			</DialogActions>
		</Dialog>
	)
}

export default ShopUserInfo
